import type { FC } from "react";

import { SettingsTabs } from "components/forms/section-settings/SettingsTabs";
import { SectionKind } from "components/server-types";
import { t } from "i18n/i18n";
import type { ResourceKey } from "i18n/i18next";
import { Banner } from "mds/components/Banner";
import { Dialog } from "mds/components/Dialog";
import { useAppDispatch, useAppSelector } from "store/index";
import {
  courseSelectors,
  selectDesignatedSection,
  selectSectionSettingsTabIndex,
} from "store/selectors";
import { viewStateActions } from "store/slices/view";
import { useObjectSelector } from "store/store-hooks";
import { getCourseAndSectionDisplayTitle } from "utils/course";
import { useIsArchived } from "utils/hooks/use-is-archived";
import { isLinkedSection } from "utils/publishing";
import { isAVEnabled } from "utils/waffle-flags";

// Used to open the Section grade tab from the SectionScoreCard.
export const SECTION_SETTINGS_GRADE_TAB_INDEX = 2;

export const SectionSettingsModal: FC = () => {
  const dispatch = useAppDispatch();
  const tabIndex = useAppSelector(selectSectionSettingsTabIndex);
  const designatedSection = useObjectSelector(selectDesignatedSection, [
    "id",
    "archived_at",
    "course_id",
    "kind",
    "template_id",
    "term_id",
    "title",
  ]);
  const { isArchived } = useIsArchived();
  const course = useObjectSelector(
    (s) => (designatedSection ? courseSelectors.selectById(s, designatedSection.course_id) : null),
    ["code", "title"],
  );
  const sectionIsArchived = designatedSection ? isArchived(designatedSection) : false;
  const sectionIsLinked = isLinkedSection(designatedSection);

  const isOpen = tabIndex !== undefined;

  const closeSectionSettingsModal = () => {
    dispatch(viewStateActions.setSectionSettingsTabIndex(undefined));
  };

  const setTabIndex = (index: number) => {
    dispatch(viewStateActions.setSectionSettingsTabIndex(index));
  };

  const sectionKind = designatedSection?.kind ?? SectionKind.UNLINKED;
  const modalTitleKey = `section.titles.${sectionKind}` as ResourceKey;
  const sectionSubtitle =
    designatedSection && course
      ? getCourseAndSectionDisplayTitle(course, designatedSection)
      : (designatedSection?.title ?? "");

  const dialogPanelClassName = isAVEnabled() ? "w-[550px]" : "w-[450px]";

  return (
    <Dialog
      // Section settings modal needs to appear above screenshare
      className="z-[52]"
      description={
        sectionSubtitle ? (
          <span className="mt-1 block text-sm font-normal leading-normal text-black-tint-40">
            {sectionSubtitle}
          </span>
        ) : undefined
      }
      dialogPanelClassName={dialogPanelClassName}
      open={isOpen}
      title={t(modalTitleKey)}
      onClose={closeSectionSettingsModal}
    >
      {sectionIsLinked && (
        <Banner className="mt-3" kind="info">
          {t("settings.section_linked_banner")}
        </Banner>
      )}

      {sectionIsArchived && (
        <Banner className="mt-3" kind="info">
          {t("settings.section_archived_banner")}
        </Banner>
      )}

      {/*
      I (David) tried to use the `<TabBar>` component from MDS for this tab bar,
      but found a strange bug: if you switch to the Course Score tab, fill out
      the form with invalid values (e.g. percentages that do not add up to 100),
      and attempt to submit, then the form does *not* display the error message, as it
      is supposed to. Instead, it resets the values in the form to the existing values
      from the course, with no visual feedback at all. I suspect this is due to some kind
      of re-rendering issue with the `<TabBar>` component, but I couldn't figure out
      how to fix it. So I am using the tab components from Headless UI instead,
      which do not have this issue.
      (`<TabBar>` is a wrapper around the Headless UI tab components, anyway.)

      Form state lives in SettingsTabs, which unmounts when the dialog closes so each
      open starts from fresh section defaults.
      */}
      {isOpen && designatedSection && tabIndex !== undefined && (
        <SettingsTabs
          key={designatedSection.id}
          sectionId={designatedSection.id}
          setTabIndex={setTabIndex}
          tabIndex={tabIndex}
          onClose={closeSectionSettingsModal}
        />
      )}
    </Dialog>
  );
};
